import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography, LinearProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import editFill from '@iconify/icons-eva/edit-fill';
import trash2Fill from '@iconify/icons-eva/trash-2-fill';
import Page from '../Page';
import PageHeader from './shared/PageHeader';
import HtmlEditor from './shared/HtmlEditor';
import {
  listMessageTemplates, createMessageTemplate, updateMessageTemplate, deleteMessageTemplate,
} from '../../utils/outreachApi';
import { showSuccessToast, showErrorToast } from '../../utils/Toast/SnackbarProvider';

const CHANNELS = [
  { value: 'EMAIL',            label: 'Email' },
  { value: 'LINKEDIN_INVITE',  label: 'LinkedIn Invite' },
  { value: 'LINKEDIN_MESSAGE', label: 'LinkedIn Message' },
];

const channelColor = { EMAIL: 'primary', LINKEDIN_INVITE: 'info', LINKEDIN_MESSAGE: 'info' };

const EMPTY_FORM = { name: '', channel: 'EMAIL', subject: '', body_html: '', body_plain: '' };

const MessageTemplates = () => {
  const [rows, setRows]       = useState(null);
  const [dialog, setDialog]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  const [channelFilter, setChannelFilter] = useState('all');

  const load = () => {
    setRows(null);
    listMessageTemplates().then(({ data }) => setRows(data || []));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialog(true); };
  const openEdit   = (t) => { setEditing(t); setForm({ name: t.name, channel: t.channel, subject: t.subject, body_html: t.body_html, body_plain: t.body_plain }); setDialog(true); };
  const closeDialog = () => { setDialog(false); setEditing(null); setForm(EMPTY_FORM); };

  const onSave = async () => {
    if (!form.name || !form.channel) return;
    setSaving(true);
    try {
      if (editing) {
        await updateMessageTemplate(editing.id, form);
        showSuccessToast('Template updated.');
      } else {
        await createMessageTemplate(form);
        showSuccessToast('Template created.');
      }
      closeDialog();
      load();
    } catch (err) {
      showErrorToast(err);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await deleteMessageTemplate(t.id);
      showSuccessToast('Template deleted.');
      load();
    } catch (err) {
      showErrorToast(err);
    }
  };

  const filtered = rows?.filter((r) => channelFilter === 'all' || r.channel === channelFilter);

  return (
    <Page title="Message Templates | CRM">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <PageHeader
          title="Message Templates"
          subtitle="Reusable email and LinkedIn copy for campaign sequences"
          actions={
            <Button variant="contained" startIcon={<Icon icon={plusFill} />} onClick={openCreate}>
              New Template
            </Button>
          }
        />

        <Card sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {['all', ...CHANNELS.map((c) => c.value)].map((v) => (
              <Button key={v} size="small"
                      variant={channelFilter === v ? 'contained' : 'outlined'}
                      onClick={() => setChannelFilter(v)}>
                {v === 'all' ? 'All' : CHANNELS.find((c) => c.value === v)?.label}
              </Button>
            ))}
          </Stack>

          {!filtered ? <LinearProgress /> : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Body preview</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={CHANNELS.find((c) => c.value === t.channel)?.label || t.channel}
                              color={channelColor[t.channel] || 'default'} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{t.subject || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="caption" color="text.secondary" noWrap
                                    dangerouslySetInnerHTML={{ __html: t.body_html?.substring(0, 120) || t.body_plain?.substring(0, 120) || '—' }} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(t)}>
                              <Icon icon={editFill} width={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => onDelete(t)}>
                              <Icon icon={trash2Fill} width={16} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          No templates yet — click "New Template" to create one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        <Dialog open={dialog} onClose={closeDialog} maxWidth="md" fullWidth>
          <DialogTitle>{editing ? 'Edit template' : 'New message template'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Name" fullWidth size="small" value={form.name}
                         onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <TextField select label="Channel" fullWidth size="small" value={form.channel}
                         onChange={(e) => setForm({ ...form, channel: e.target.value })}>
                {CHANNELS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </TextField>
              {form.channel === 'EMAIL' && (
                <TextField label="Subject line" fullWidth size="small" value={form.subject}
                           onChange={(e) => setForm({ ...form, subject: e.target.value })}
                           helperText="Supports {{first_name}}, {{company_name}}, {{pm_name}}" />
              )}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
                  Body {form.channel === 'EMAIL' ? '(HTML)' : '(plain text)'}
                </Typography>
                <HtmlEditor
                  value={form.body_html}
                  onChange={(html) => setForm({ ...form, body_html: html })}
                  placeholder={form.channel === 'EMAIL'
                    ? 'Hi {{first_name}},\n\nSaw {{company_name}} is doing interesting work…'
                    : 'Hi {{first_name}}, would love to connect re: {{company_name}}.'}
                  minHeight={220}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Use the "Insert variable" menu in the toolbar to drop substitution tokens.
                </Typography>
              </Box>
              {form.channel === 'EMAIL' && (
                <TextField label="Plain-text fallback (optional)" fullWidth multiline rows={4}
                           size="small" value={form.body_plain}
                           onChange={(e) => setForm({ ...form, body_plain: e.target.value })} />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button variant="contained" disabled={!form.name || saving} onClick={onSave}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
};

export default MessageTemplates;
